import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, useTheme } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Building2, Moon, Sun, Globe, ChevronRight, ChevronLeft, Check, CreditCard } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var languages = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "nl", name: "Nederlands", flag: "🇳🇱" },
  { code: "es", name: "Español", flag: "🇪🇸" },
];

var countries = [
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
];

export default function Register() {
  var navigate = useNavigate();
  var { user, login } = useAuth();
  var { theme, toggleTheme } = useTheme();
  var { t, i18n } = useTranslation();
  var [loading, setLoading] = useState(false);
  var [step, setStep] = useState(1); // 1 = Personal Info, 2 = Payment
  var [subscriptionSettings, setSubscriptionSettings] = useState(null);
  var [registeredUser, setRegisteredUser] = useState(null);

  // Form fields
  var [firstName, setFirstName] = useState("");
  var [lastName, setLastName] = useState("");
  var [email, setEmail] = useState("");
  var [password, setPassword] = useState("");
  var [confirmPassword, setConfirmPassword] = useState("");
  var [address, setAddress] = useState("");
  var [zipcode, setZipcode] = useState("");
  var [country, setCountry] = useState("");
  var [mobile, setMobile] = useState("");
  var [companyName, setCompanyName] = useState("");
  var [vatNumber, setVatNumber] = useState("");

  useEffect(function() { 
    if (user) navigate("/dashboard"); 
    fetchSubscriptionSettings();
    
    // Handle PayPal return flows
    var urlParams = new URLSearchParams(window.location.search);
    
    // Handle PayPal subscription success
    if (urlParams.get("paypal_subscription_success") === "true") {
      var subId = urlParams.get("sub_id");
      var storedUser = localStorage.getItem("pending_registration");
      if (subId && storedUser) {
        handlePayPalSubscriptionReturn(subId, JSON.parse(storedUser));
      }
    }
    
    // Handle PayPal one-time payment success
    if (urlParams.get("paypal_success") === "true") {
      var orderId = urlParams.get("order_id");
      var storedOrderUser = localStorage.getItem("pending_registration");
      var storedOrder = localStorage.getItem("paypal_order");
      if (orderId && storedOrderUser && storedOrder) {
        handlePayPalOrderReturn(orderId, JSON.parse(storedOrderUser), JSON.parse(storedOrder));
      }
    }
    
    // Handle PayPal cancellation
    if (urlParams.get("paypal_cancelled") === "true" || urlParams.get("paypal_subscription_cancelled") === "true") {
      toast.info(t("payment.paypalCancelled"));
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user, navigate]);

  async function handlePayPalSubscriptionReturn(subscriptionId, userData) {
    setLoading(true);
    try {
      var response = await fetch(API_URL + "/api/payments/paypal/activate-subscription/" + subscriptionId, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      
      var data = await response.json();
      
      if (response.ok && (data.status === "ACTIVE" || data.status === "already_active")) {
        localStorage.removeItem("pending_registration");
        localStorage.removeItem("paypal_subscription");
        login(userData, userData.token);
        toast.success(t("payment.subscriptionActivated"));
        navigate("/dashboard");
      } else {
        toast.error(data.detail || t("payment.activationFailed"));
      }
    } catch (err) {
      toast.error(t("payment.error"));
    } finally {
      setLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  async function handlePayPalOrderReturn(orderId, userData, orderData) {
    setLoading(true);
    try {
      var response = await fetch(API_URL + "/api/payments/paypal/capture/" + orderId, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paypal_order_id: orderData.paypal_order_id
        })
      });
      
      var data = await response.json();
      
      if (response.ok && (data.status === "success" || data.status === "already_processed")) {
        localStorage.removeItem("pending_registration");
        localStorage.removeItem("paypal_order");
        login(userData, userData.token);
        toast.success(t("payment.success"));
        navigate("/dashboard");
      } else {
        toast.error(data.detail || t("payment.error"));
      }
    } catch (err) {
      toast.error(t("payment.error"));
    } finally {
      setLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  async function fetchSubscriptionSettings() {
    try {
      var response = await fetch(API_URL + "/api/settings/subscription");
      if (response.ok) {
        setSubscriptionSettings(await response.json());
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  }

  async function handleRegistration(e) {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t("auth.passwordMismatch"));
      return;
    }
    
    if (password.length < 8) {
      toast.error(t("auth.passwordTooShort"));
      return;
    }
    
    setLoading(true);
    try {
      var response = await fetch(API_URL + "/api/auth/register-full", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email: email,
          password: password,
          address: address,
          zipcode: zipcode,
          country: country,
          mobile: mobile,
          company_name: companyName || null,
          vat_number: vatNumber || null
        })
      });
      
      var data = await response.json();
      
      if (response.ok) {
        setRegisteredUser(data);
        
        if (data.requires_payment) {
          // Move to payment step
          setStep(2);
          toast.success(t("auth.registrationSuccess"));
        } else {
          // First user (admin) - no payment required
          login(data, data.token);
          toast.success(t("auth.accountCreated"));
          navigate("/dashboard");
        }
      } else {
        toast.error(data.detail || t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  }

  async function handleStripePayment() {
    if (!registeredUser) return;
    
    setLoading(true);
    try {
      var response = await fetch(API_URL + "/api/payments/stripe/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: registeredUser.user_id,
          origin_url: window.location.origin
        })
      });
      
      var data = await response.json();
      
      if (response.ok && data.checkout_url) {
        // Store user data for after payment
        localStorage.setItem("pending_registration", JSON.stringify(registeredUser));
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || t("payment.error"));
      }
    } catch (err) {
      toast.error(t("payment.error"));
    } finally {
      setLoading(false);
    }
  }

  async function handlePayPalPayment() {
    if (!registeredUser) return;
    
    setLoading(true);
    try {
      // First try to create a recurring subscription
      var response = await fetch(API_URL + "/api/payments/paypal/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: registeredUser.user_id,
          origin_url: window.location.origin
        })
      });
      
      var data = await response.json();
      
      if (response.ok && data.approval_url) {
        // Store for return flow
        localStorage.setItem("pending_registration", JSON.stringify(registeredUser));
        localStorage.setItem("paypal_subscription", JSON.stringify(data));
        toast.info(t("payment.paypalRedirect"));
        // Redirect to PayPal approval
        window.location.href = data.approval_url;
      } else if (response.status === 400 && data.detail && data.detail.includes("plan not configured")) {
        // Fallback to one-time payment if subscription not set up
        toast.info(t("payment.usingOneTimePayment"));
        await handlePayPalOneTimePayment();
      } else {
        toast.error(data.detail || t("payment.error"));
      }
    } catch (err) {
      toast.error(t("payment.error"));
    } finally {
      setLoading(false);
    }
  }

  async function handlePayPalOneTimePayment() {
    if (!registeredUser) return;
    
    try {
      var response = await fetch(API_URL + "/api/payments/paypal/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: registeredUser.user_id,
          origin_url: window.location.origin
        })
      });
      
      var data = await response.json();
      
      if (response.ok && data.approval_url) {
        localStorage.setItem("pending_registration", JSON.stringify(registeredUser));
        localStorage.setItem("paypal_order", JSON.stringify(data));
        toast.info(t("payment.paypalRedirect"));
        window.location.href = data.approval_url;
      } else {
        toast.error(data.detail || t("payment.error"));
      }
    } catch (err) {
      toast.error(t("payment.error"));
    }
  }

  function handleSkipPayment() {
    // User chooses to use trial period
    if (registeredUser) {
      login(registeredUser, registeredUser.token);
      toast.success(t("auth.trialStarted"));
      navigate("/dashboard");
    }
  }

  function handleLanguageChange(langCode) { 
    i18n.changeLanguage(langCode); 
  }

  var currentLang = i18n.language || "en";
  var currentLangData = languages.find(function(l) { return l.code === currentLang; }) || languages[0];
  var trialDays = subscriptionSettings?.trial_days || 14;
  var monthlyPrice = subscriptionSettings?.monthly_price || 29.99;
  var currency = subscriptionSettings?.currency || "eur";

  function renderCountryOptions() {
    var items = [];
    for (var i = 0; i < countries.length; i++) {
      items.push(<SelectItem key={countries[i].code} value={countries[i].code}>{countries[i].name}</SelectItem>);
    }
    return items;
  }

  if (user) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 border-b bg-background/80 backdrop-blur-lg">
        <div className="container mx-auto px-6 h-32 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={function() { navigate("/"); }}>
            <img src="https://customer-assets.emergentagent.com/job_bd8042f0-b1af-4ffb-b95c-6b13c2cafb43/artifacts/citl3dkd_logo_basic.png" alt="Real Estate Manager" className="h-28 w-auto" />
          </div>
          <div className="flex items-center gap-2">
            <Select value={currentLang} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-auto gap-2" data-testid="register-language-select">
                <Globe className="w-4 h-4" />
                <span className="hidden sm:inline">{currentLangData.flag} {currentLangData.name}</span>
              </SelectTrigger>
              <SelectContent>
                {languages.map(function(lang) { return <SelectItem key={lang.code} value={lang.code}><span className="mr-2">{lang.flag}</span>{lang.name}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}</Button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center gap-4">
              <div className={"flex items-center gap-2 " + (step >= 1 ? "text-primary" : "text-muted-foreground")}>
                <div className={"w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium " + (step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted")}>
                  {step > 1 ? <Check className="w-4 h-4" /> : "1"}
                </div>
                <span className="hidden sm:inline font-medium">{t("auth.personalInfo")}</span>
              </div>
              <div className={"w-16 h-0.5 " + (step >= 2 ? "bg-primary" : "bg-muted")} />
              <div className={"flex items-center gap-2 " + (step >= 2 ? "text-primary" : "text-muted-foreground")}>
                <div className={"w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium " + (step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted")}>2</div>
                <span className="hidden sm:inline font-medium">{t("auth.payment")}</span>
              </div>
            </div>
          </div>

          {step === 1 && (
            <Card className="border-2" data-testid="registration-form-card">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">{t("auth.createYourAccount")}</CardTitle>
                <CardDescription>{t("auth.fillInDetails")}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegistration} className="space-y-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t("auth.personalInfo")}</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="form-label">{t("auth.firstName")} *</Label>
                        <Input value={firstName} onChange={function(e) { setFirstName(e.target.value); }} placeholder="John" required data-testid="reg-firstname-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="form-label">{t("auth.lastName")} *</Label>
                        <Input value={lastName} onChange={function(e) { setLastName(e.target.value); }} placeholder="Doe" required data-testid="reg-lastname-input" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="form-label">{t("common.email")} *</Label>
                        <Input type="email" value={email} onChange={function(e) { setEmail(e.target.value); }} placeholder="john@example.com" required data-testid="reg-email-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="form-label">{t("auth.mobile")} *</Label>
                        <Input type="tel" value={mobile} onChange={function(e) { setMobile(e.target.value); }} placeholder="+31 6 12345678" required data-testid="reg-mobile-input" />
                      </div>
                    </div>
                  </div>

                  {/* Address Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t("auth.addressInfo")}</h3>
                    <div className="space-y-2">
                      <Label className="form-label">{t("auth.address")} *</Label>
                      <Input value={address} onChange={function(e) { setAddress(e.target.value); }} placeholder="123 Main Street" required data-testid="reg-address-input" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="form-label">{t("auth.zipcode")} *</Label>
                        <Input value={zipcode} onChange={function(e) { setZipcode(e.target.value); }} placeholder="1234 AB" required data-testid="reg-zipcode-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="form-label">{t("auth.country")} *</Label>
                        <Select value={country} onValueChange={setCountry} required>
                          <SelectTrigger data-testid="reg-country-select"><SelectValue placeholder={t("auth.selectCountry")} /></SelectTrigger>
                          <SelectContent>{renderCountryOptions()}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Company Information (Optional) */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t("auth.companyInfo")} ({t("common.optional")})</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="form-label">{t("auth.companyName")}</Label>
                        <Input value={companyName} onChange={function(e) { setCompanyName(e.target.value); }} placeholder="ACME Real Estate" data-testid="reg-company-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="form-label">{t("auth.vatNumber")}</Label>
                        <Input value={vatNumber} onChange={function(e) { setVatNumber(e.target.value); }} placeholder="NL123456789B01" data-testid="reg-vat-input" />
                      </div>
                    </div>
                  </div>

                  {/* Password */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">{t("auth.security")}</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="form-label">{t("common.password")} *</Label>
                        <Input type="password" value={password} onChange={function(e) { setPassword(e.target.value); }} placeholder="••••••••" required data-testid="reg-password-input" />
                      </div>
                      <div className="space-y-2">
                        <Label className="form-label">{t("auth.confirmPassword")} *</Label>
                        <Input type="password" value={confirmPassword} onChange={function(e) { setConfirmPassword(e.target.value); }} placeholder="••••••••" required data-testid="reg-confirm-password-input" />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">{t("auth.passwordRequirements")}</p>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button type="button" variant="outline" onClick={function() { navigate("/"); }} className="flex-1">
                      <ChevronLeft className="w-4 h-4 mr-2" />{t("common.back")}
                    </Button>
                    <Button type="submit" className="flex-1" disabled={loading || !country} data-testid="reg-submit-btn">
                      {loading ? t("auth.creating") : t("auth.continue")}<ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {step === 2 && (
            <Card className="border-2" data-testid="payment-card">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">{t("auth.choosePaymentMethod")}</CardTitle>
                <CardDescription>{t("auth.subscriptionInfo", { price: monthlyPrice, currency: currency.toUpperCase() })}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Trial Info */}
                <div className="p-4 rounded-lg bg-accent/10 border border-accent/20 text-center">
                  <p className="font-medium text-accent">{t("auth.trialPeriodInfo", { days: trialDays })}</p>
                  <p className="text-sm text-muted-foreground mt-1">{t("auth.noChargeUntilTrialEnds")}</p>
                </div>

                {/* Payment Options */}
                <div className="space-y-4">
                  <Button onClick={handleStripePayment} className="w-full h-14 gap-3" disabled={loading} data-testid="stripe-payment-btn">
                    <CreditCard className="w-5 h-5" />
                    <span>{t("payment.payWithCard")}</span>
                  </Button>
                  
                  <Button onClick={handlePayPalPayment} variant="outline" className="w-full h-14 gap-3" disabled={loading} data-testid="paypal-payment-btn">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.64h6.2c2.069 0 3.534.4 4.36 1.191.826.791 1.103 1.893.825 3.282-.28 1.404-.898 2.547-1.837 3.398-.939.85-2.199 1.276-3.744 1.276H9.13l-1.054 5.53a.77.77 0 0 1-.757.64h-.243Z"/>
                      <path d="M19.05 7.77c-.28 1.404-.898 2.547-1.837 3.398-.939.85-2.199 1.276-3.744 1.276h-1.91l-1.054 5.53a.641.641 0 0 1-.633.541H7.33l.243-1.276.81-4.254h2.123c2.069 0 3.534-.4 4.36-1.191.826-.791 1.103-1.893.825-3.282l-.001-.002c.826.791 1.103 1.893.825 3.282l.535-.022Z" opacity="0.5"/>
                    </svg>
                    <span>{t("payment.payWithPayPal")}</span>
                  </Button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-muted-foreground">{t("common.or")}</span></div>
                </div>

                <Button onClick={handleSkipPayment} variant="ghost" className="w-full" data-testid="skip-payment-btn">
                  {t("auth.startTrialWithoutPayment")}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  {t("auth.paymentTerms")}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
